import { useState } from 'react'
import { startOfMonth, endOfDay, format } from 'date-fns'
import { apiAdminStagingWinLoss, apiAdminStagingExportWinLoss, ApiRequestError } from '@/lib/api'
import type { WinLossReport } from '@nexbit/types'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { cn, formatCurrency } from '@/lib/utils'
import { AlertCircle, Download, Search, TrendingUp } from 'lucide-react'

/**
 * Win/loss for the staging line on its own.
 *
 * The ordinary report still counts these agents — that was the call, so the numbers add
 * up the same way they always did. This page is how you see how much of them is test
 * traffic, which is why it says so at the top rather than pretending to be a separate set
 * of books.
 */

const toDateInput = (d: Date) => format(d, 'yyyy-MM-dd')
const signed = (v: string) => (parseFloat(v) >= 0 ? 'text-success font-semibold' : 'text-destructive font-semibold')

export default function StagingReportsPage() {
  const [from, setFrom] = useState(toDateInput(startOfMonth(new Date())))
  const [to, setTo] = useState(toDateInput(endOfDay(new Date())))
  const [rows, setRows] = useState<WinLossReport[] | null>(null)
  const [applied, setApplied] = useState<{ from: string; to: string } | null>(null)
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    setLoading(true)
    setError(null)
    try {
      const range = { from: new Date(from).toISOString(), to: new Date(`${to}T23:59:59`).toISOString() }
      setRows(await apiAdminStagingWinLoss(range))
      setApplied(range)
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'โหลดรายงานไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }

  async function handleExport() {
    if (!applied) return
    setExporting(true)
    try {
      await apiAdminStagingExportWinLoss(applied)
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'ส่งออกไม่สำเร็จ')
    } finally {
      setExporting(false)
    }
  }

  const inputClass = cn(
    'h-9 rounded-[var(--radius-sm)] border border-input bg-background px-3 text-sm',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
  )

  const totals = rows?.reduce(
    (acc, r) => ({
      bet: acc.bet + parseFloat(r.totalBetAmount),
      win: acc.win + parseFloat(r.totalWinAmount),
      net: acc.net + parseFloat(r.netProfit),
    }),
    { bet: 0, win: 0, net: 0 },
  )

  return (
    <div className="max-w-5xl p-4 md:p-6">
      <h1 className="text-lg font-semibold tracking-tight">รายงานสายทดสอบ</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        ยอดเดิมพันของเอเจนต์ในสาย staging เท่านั้น
      </p>

      <div className="mt-3 flex items-start gap-2 rounded-[var(--radius-sm)] border border-warning/40 bg-warning/10 px-3 py-2.5 text-xs leading-relaxed text-foreground/80">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
        <span>
          ข้อมูลชุดนี้เป็นสายทดสอบ และ<strong>ยังถูกนับรวมในเมนูรายงานปกติด้วย</strong> —
          ถ้าต้องการยอดที่ไม่รวมสายทดสอบ ให้เอาตัวเลขจากหน้านี้ไปหักออก
        </span>
      </div>

      <form onSubmit={(e) => void handleSearch(e)} className="mt-4 overflow-hidden rounded-[var(--radius)] border border-border bg-card shadow-[var(--shadow-sm)]">
        <div className="border-b border-border bg-secondary/40 px-4 py-2.5 text-sm font-semibold">กรอง</div>
        <div className="flex flex-wrap items-end gap-3 p-4">
          <div>
            <label htmlFor="sr-from" className="mb-1 block text-xs text-muted-foreground">ตั้งแต่</label>
            <input id="sr-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={inputClass} />
          </div>
          <div>
            <label htmlFor="sr-to" className="mb-1 block text-xs text-muted-foreground">ถึง</label>
            <input id="sr-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className={inputClass} />
          </div>
          <Button type="submit" size="sm" className="gap-1.5">
            <Search className="h-3.5 w-3.5" />
            ค้นหา
          </Button>
        </div>
      </form>

      {error && (
        <div role="alert" className="mt-4 flex items-start gap-2 rounded-[var(--radius-sm)] border border-destructive/40 bg-destructive/5 px-3 py-2.5 text-sm text-destructive">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <div className="mt-4 space-y-2">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-9 w-full" />)}
        </div>
      ) : rows === null ? (
        <p className="mt-4 rounded-[var(--radius)] border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          เลือกช่วงวันที่แล้วกดค้นหาเพื่อดูรายงาน
        </p>
      ) : (
        <section className="mt-4 overflow-hidden rounded-[var(--radius)] border border-border bg-card shadow-[var(--shadow-sm)]">
          <div className="flex items-center gap-2 border-b border-border bg-secondary/40 px-4 py-3">
            <TrendingUp className="h-4 w-4 text-primary" />
            <span className="text-sm font-semibold">ผลลัพธ์</span>
            <span className="text-xs text-muted-foreground">{rows.length} เอเจนต์</span>
            {rows.length > 0 && (
              <Button variant="outline" size="sm" className="ml-auto gap-1.5" onClick={() => void handleExport()}>
                <Download className="h-3.5 w-3.5" />
                {exporting ? 'กำลังส่งออก...' : 'ส่งออก CSV'}
              </Button>
            )}
          </div>

          {rows.length === 0 ? (
            <p className="p-8 text-center text-sm text-muted-foreground">ไม่พบข้อมูลในช่วงที่เลือก</p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>เอเจนต์</TableHead>
                      <TableHead className="text-right">ยอดเดิมพัน</TableHead>
                      <TableHead className="text-right">ยอดจ่าย</TableHead>
                      <TableHead className="text-right">กำไรสุทธิ</TableHead>
                      <TableHead className="text-right">จำนวนรอบ</TableHead>
                      <TableHead className="text-right">สมาชิก</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((r) => (
                      <TableRow key={r.partnerId}>
                        <TableCell className="font-medium">{r.partnerCode}</TableCell>
                        <TableCell className="text-right font-mono text-sm">{formatCurrency(r.totalBetAmount)}</TableCell>
                        <TableCell className="text-right font-mono text-sm">{formatCurrency(r.totalWinAmount)}</TableCell>
                        <TableCell className={cn('text-right font-mono text-sm', signed(r.netProfit))}>
                          {formatCurrency(r.netProfit)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">{r.roundCount}</TableCell>
                        <TableCell className="text-right font-mono text-sm">{r.memberCount}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {totals && (
                <div className="flex flex-wrap items-center justify-end gap-6 border-t border-border bg-secondary/20 px-4 py-2.5 text-xs">
                  <span>ยอดเดิมพันรวม <span className="font-mono font-semibold">{formatCurrency(String(totals.bet))}</span></span>
                  <span>ยอดจ่ายรวม <span className="font-mono font-semibold">{formatCurrency(String(totals.win))}</span></span>
                  <span>กำไรสุทธิรวม <span className={cn('font-mono', signed(String(totals.net)))}>{formatCurrency(String(totals.net))}</span></span>
                </div>
              )}
            </>
          )}
        </section>
      )}
    </div>
  )
}
